import React from "react";
import { Box, Typography } from "@mui/material";
import moment from "moment";
import type { AuditShiftActivityItem } from "models/audit/AuditDetailModel";
import { padLeftNumeric, parseBarcodeFormat } from "utils/barcodeFormat";
import { auditDashboardTheme } from "../../theme";

const PHONE_DETECTION_BREAK = "\u200C";

const formatActivityTime = (activity: AuditShiftActivityItem) => {
  if (activity.shift_date && activity.shift_time) {
    const parsed = moment(`${activity.shift_date}T${activity.shift_time}`);
    if (parsed.isValid()) {
      return parsed.format("M/D h:mm A");
    }
  }

  const parsed = moment.parseZone(activity.created_at);
  return parsed.isValid() ? parsed.format("M/D h:mm A") : "-";
};

const formatActivityAction = (activity: AuditShiftActivityItem) => {
  if (activity.event_type === "pack_loaded") {
    return "Pack loaded";
  }

  if (activity.event_type === "pack_removed") {
    return activity.removed_type === "sold_out" ? "Pack sold out" : "Pack removed";
  }

  return "Pack activity";
};

const formatGamePackValue = (activity: AuditShiftActivityItem) => {
  if (!activity.game_id || !activity.pack_id) {
    return "-";
  }

  try {
    const pattern = parseBarcodeFormat(activity.barcode_format);
    const game = padLeftNumeric(activity.game_id, pattern.game.length).slice(-pattern.game.length);
    const pack = padLeftNumeric(activity.pack_id, pattern.pack.length).slice(-pattern.pack.length);
    return `${game}-${PHONE_DETECTION_BREAK}${pack}`;
  } catch (_error) {
    return `${activity.game_id}-${PHONE_DETECTION_BREAK}${activity.pack_id}`;
  }
};

const formatOperator = (activity: AuditShiftActivityItem) =>
  activity.operator_name || activity.created_by_name || "-";

interface ShiftActivityProps {
  activities?: AuditShiftActivityItem[];
}

export default function ShiftActivity({ activities = [] }: ShiftActivityProps) {
  if (!activities.length) {
    return null;
  }

  return (
    <Box
      component="section"
      aria-label="Shift activity"
      sx={{
        mx: "12px",
        pt: "12px",
        borderTop: `1px solid ${auditDashboardTheme.colors.lineSoft}`,
        bgcolor: "transparent",
        pb: "8px"
      }}
    >
      <Typography
        component="div"
        sx={{
          mb: "12px",
          color: auditDashboardTheme.colors.text,
          fontSize: 13,
          fontWeight: 800,
          lineHeight: 1,
          textDecoration: "underline",
          textTransform: "uppercase",
        }}
      >
        Shift Activity
      </Typography>

      <Box sx={{ display: "grid", gap: 0 }}>
        {activities.map((activity) => {
          const activityKey =
            activity.event_id ||
            activity.activity_id ||
            `${activity.created_at}-${activity.event_type}-${activity.game_id}-${activity.pack_id}`;
          const gamePack = formatGamePackValue(activity);

          return (
            <Box
              key={activityKey}
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "110px minmax(132px, 1fr)",
                  sm: "118px 136px minmax(240px, 1fr) 150px",
                },
                gap: "10px",
                alignItems: "baseline",
                minHeight: 24,
                color: auditDashboardTheme.colors.textMutedStrong,
                fontSize: 14,
                fontWeight: 600,
                lineHeight: 1.25,
                minWidth: { xs: 0, sm: 680 },
              }}
            >
              <Box component="span" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                {formatActivityTime(activity)}
              </Box>
              <Box component="span" sx={{ color: auditDashboardTheme.colors.text, fontWeight: 700, whiteSpace: "nowrap" }}>
                {formatActivityAction(activity)}
              </Box>
              <Box
                component="span"
                sx={{
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontWeight: 600,
                }}
              >
                Game{" "}
                <Box
                  component="span"
                  sx={{
                    fontFamily: '"Roboto Mono", "SFMono-Regular", Consolas, monospace',
                    fontVariantNumeric: "tabular-nums",
                    letterSpacing: 0,
                  }}
                >
                  {gamePack}
                </Box>
                {activity.slot_id ? ` - Bin ${activity.slot_id}` : ""}
              </Box>
              <Box
                component="span"
                sx={{
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontWeight: 600,
                  gridColumn: { xs: "2", sm: "auto" },
                }}
              >
                {`By ${formatOperator(activity)}`}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
